import {
	AnalyticsPlatformParams,
	AnalyticsRangeParams,
	CrossFlowFunnel,
	CrossFlowFunnelParams,
	DropOffPoint,
	EnergyChartHealth,
	FeatureAdoption,
	FlowError,
	FlowErrorsParams,
	FlowFunnel,
	FlowFunnelParams,
	PlatformUsersPoint,
	SessionsOverview,
	VendRetentionCohort,
	WeeklyFlags,
	WeeklyFlagsParams,
} from "../model/bps-admin/business-analytics";
import { apiSlice } from "../stores/base/api-slice";

const serviceBaseUrl = "/businessanalytics";

// An absent utilityId is every utility — the key has to be left out entirely rather
// than sent empty.
const rangeParams = ({ dateFrom, dateTo, utilityId }: AnalyticsRangeParams) => ({
	dateFrom,
	dateTo,
	...(utilityId ? { utilityId } : {}),
});

// An absent platform is every platform, on the same terms as an absent utilityId.
const platformParams = ({ platform, ...range }: AnalyticsPlatformParams) => ({
	...rangeParams(range),
	...(platform ? { platform } : {}),
});

// No providesTags anywhere here: nothing mutates analytics, so there is no tag to
// invalidate.
const businessAnalyticsEndpoints = apiSlice.injectEndpoints({
	endpoints: builder => ({
		getFlowFunnel: builder.query<FlowFunnel, FlowFunnelParams>({
			query: ({ code, getErrors, ...params }) => ({
				url: `${serviceBaseUrl}/flows/${code}/funnel`,
				method: "GET",
				params: { ...platformParams(params), getErrors },
			}),
		}),
		getCrossFlowFunnel: builder.query<CrossFlowFunnel, CrossFlowFunnelParams>({
			query: ({ code, secondFlowCode, ...params }) => ({
				url: `${serviceBaseUrl}/flows/${code}/cross-funnel`,
				method: "GET",
				params: { ...platformParams(params), secondFlowCode },
			}),
		}),
		getFlowErrors: builder.query<FlowError[], FlowErrorsParams>({
			query: ({ code, ...params }) => ({
				url: `${serviceBaseUrl}/flows/${code}/top-errors`,
				method: "GET",
				params: platformParams(params),
			}),
		}),
		getEnergyChartHealth: builder.query<EnergyChartHealth, AnalyticsPlatformParams>({
			query: params => ({
				url: `${serviceBaseUrl}/flows/energy-chart-health`,
				method: "GET",
				params: platformParams(params),
			}),
		}),
		getDropOffPoints: builder.query<DropOffPoint[], AnalyticsPlatformParams>({
			query: params => ({
				url: `${serviceBaseUrl}/flows/drop-off-points`,
				method: "GET",
				params: platformParams(params),
			}),
		}),
		getFeatureAdoption: builder.query<FeatureAdoption[], AnalyticsPlatformParams>({
			query: params => ({
				url: `${serviceBaseUrl}/flows/feature-adoption`,
				method: "GET",
				params: platformParams(params),
			}),
		}),
		getVendRetention: builder.query<VendRetentionCohort[], AnalyticsPlatformParams>({
			query: params => ({
				url: `${serviceBaseUrl}/flows/vend-retention`,
				method: "GET",
				params: platformParams(params),
			}),
		}),
		getWeeklyFlags: builder.query<WeeklyFlags, WeeklyFlagsParams>({
			query: ({ utilityId, platform }) => ({
				url: `${serviceBaseUrl}/flows/weekly-flags`,
				method: "GET",
				params: { ...(utilityId ? { utilityId } : {}), ...(platform ? { platform } : {}) },
			}),
		}),
		getPlatformUsers: builder.query<PlatformUsersPoint[], AnalyticsRangeParams>({
			query: params => ({
				url: `${serviceBaseUrl}/sessions/platform-users`,
				method: "GET",
				params: rangeParams(params),
			}),
		}),
		getSessionsOverview: builder.query<SessionsOverview, AnalyticsPlatformParams>({
			query: params => ({
				url: `${serviceBaseUrl}/sessions/overview`,
				method: "GET",
				params: platformParams(params),
			}),
		}),
	}),
});

export const {
	useGetFlowFunnelQuery,
	useGetCrossFlowFunnelQuery,
	useGetFlowErrorsQuery,
	useGetEnergyChartHealthQuery,
	useGetDropOffPointsQuery,
	useGetFeatureAdoptionQuery,
	useGetVendRetentionQuery,
	useGetWeeklyFlagsQuery,
	useGetPlatformUsersQuery,
	useGetSessionsOverviewQuery,
} = businessAnalyticsEndpoints;
